import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  BotCampaignDraftContext,
  BotCampaignStarterOption,
  CAMPAIGN_MAX_MESSAGES,
  CAMPAIGN_MESSAGE_MAX_CHARS,
  LocationVarKey,
  TenantInterface
} from "@swivl-mono/types";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Input,
  Modal,
  Select,
  SelectItem,
  Spinner,
  VariableTypeahead
} from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import TenantsModel from "../../../Models/Tenants/Tenants.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";

/**
 * SMS campaign compose (ENG-3114; spec `docs/specs/conversation-starters.md`, "Caller 2").
 *
 * Two ways to compose (Chuck, 2026-08-28). Pick one of the SMS starters and its message and context
 * arrive together. Or write the message yourself, and an extra step reads it back to you in plain
 * language — "you're saying there's a promo in July?" — which you approve or correct. Approving is
 * what writes the context the bot gets if someone replies; agents never see or write prompt text.
 *
 * Whatever is in the message box is what customers receive, word for word. The one model call
 * happens here, at compose time, never per recipient.
 *
 * Snowy-owned; the legacy BroadcastModal is untouched.
 */

type Step = 0 | 1 | 2 | 3;
// Four steps (Chuck, 2026-09-04): the separate Confirm step was one too many — its job is the
// About block on Review & Send.
const STEP_LABELS = ["Locations", "Recipients", "Compose", "Review & Send"];
type ComposeMode = "starter" | "written";

interface SnowyCampaignModalProps {
  open: boolean;
  onClose: () => void;
}

export function SnowyCampaignModal({ open, onClose }: SnowyCampaignModalProps): React.ReactNode {
  const navigate = useNavigate();
  const { locationsWithVariables } = LocationsModel.useState((s) => s);
  const { broadcastTenants, isBroadcastTenantsLoading } = TenantsModel.useState((s) => s);

  const [step, setStep] = useState<Step>(0);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [recipientIds, setRecipientIds] = useState<number[]>([]);
  // Filter boxes (Chuck, 2026-08-31): 254 locations and 1,100+ recipients as bare scroll lists were
  // unusable. Filtering is display-only — selections made under one filter survive another.
  const [locationQuery, setLocationQuery] = useState("");
  const [recipientQuery, setRecipientQuery] = useState("");
  const [starters, setStarters] = useState<BotCampaignStarterOption[] | null>(null);
  const [starterSlug, setStarterSlug] = useState<string>("");
  // Write-your-own is the default and the first card (Chuck, 2026-08-31).
  const [mode, setMode] = useState<ComposeMode>("written");
  const [messages, setMessages] = useState<string[]>([""]);
  const [sending, setSending] = useState(false);
  // Written path only: the model's read-back, the agent's correction, and whether a draft is in
  // flight. `draft` is null until they have something to approve.
  const [draft, setDraft] = useState<BotCampaignDraftContext | null>(null);
  const [intent, setIntent] = useState("");
  const [drafting, setDrafting] = useState(false);
  // Review & Send → About → "Not what you meant? Describe it" expander (describe the intent, redo).
  const [correcting, setCorrecting] = useState(false);
  // Escape inside an open variable picker closes the picker, never this modal.
  const pickerOpenRef = useRef(false);

  // The CC v3 route never loads the locations store; the modal loads it on open when empty.
  useEffect(() => {
    if (!open || (locationsWithVariables && Object.keys(locationsWithVariables).length > 0)) return;
    const load = async (): Promise<void> => {
      await LocationVarsModel.loadAllVariables(); // locationsWithVariables is only built once the vars are loaded
      await LocationsModel.getGroupsAndLocations(true);
    };
    void load();
  }, [open, locationsWithVariables]);

  const locations = useMemo(
    () =>
      Object.values(locationsWithVariables ?? {})
        .map(({ location, variables }) => {
          // The studio store keeps `{ value }` objects per var (see BroadcastModal), not bare strings.
          const nameVar = variables[LocationVarKey.Name] as unknown as { value?: string } | string | undefined;
          const name = (typeof nameVar === "string" ? nameVar : nameVar?.value) || location.name || `Location ${location.id}`;
          return { id: location.id, name: String(name) };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [locationsWithVariables]
  );

  // Recipients: tenants with a phone at the chosen locations (server call; Chuck, 2026-08-26).
  useEffect(() => {
    if (!open || step !== 1 || locationIds.length === 0) return;
    void TenantsModel.getUniqueBroadcastTenantsBy(locationIds, undefined, true);
  }, [open, step, locationIds]);

  useEffect(() => {
    if (!open || step !== 2 || starters !== null || !Member.orgId) return;
    API.SnowyCampaigns.listStarters(Member.orgId)
      .then((list) => {
        setStarters(list);
        if (list.length > 0 && !starterSlug) setStarterSlug(list[0].slug);
      })
      .catch(() => {
        setStarters([]);
        AlertsModel.errorToast("Could not load Conversation Starters");
      });
  }, [open, step, starters, starterSlug]);

  const recipients = useMemo(() => {
    const byUser = new Map<number, TenantInterface>();
    for (const t of broadcastTenants ?? []) if (t.phone && !byUser.has(t.userId)) byUser.set(t.userId, t);
    return [...byUser.values()];
  }, [broadcastTenants]);

  const shownLocations = useMemo(() => {
    const q = locationQuery.trim().toLowerCase();
    return q ? locations.filter((l) => l.name.toLowerCase().includes(q)) : locations;
  }, [locations, locationQuery]);

  const shownRecipients = useMemo(() => {
    const q = recipientQuery.trim().toLowerCase();
    if (!q) return recipients;
    const digits = q.replace(/[^0-9]/g, "");
    return recipients.filter((r) => {
      const name = (r.name || [r.firstName, r.lastName].filter(Boolean).join(" ")).toLowerCase();
      return name.includes(q) || (digits.length > 0 && (r.phone ?? "").replace(/[^0-9]/g, "").includes(digits));
    });
  }, [recipients, recipientQuery]);

  const starter = starters?.find((s) => s.slug === starterSlug) ?? null;
  const written = mode === "written";
  const customMessages = messages.map((m) => m.trim()).filter((m) => m.length > 0);
  const overLimit = messages.some((m) => m.length > CAMPAIGN_MESSAGE_MAX_CHARS);
  // Untouched starter text sends as the starter's own; edit a word and it becomes authored text.
  const editedStarter =
    !!starter && JSON.stringify(customMessages) !== JSON.stringify(starter.verbatimMessages.map((m) => m.trim()).filter((m) => m.length > 0));
  const canCompose = customMessages.length > 0 && !overLimit && (written || !!starter);
  const canNext = step === 0 ? locationIds.length > 0 : step === 1 ? recipientIds.length > 0 : step === 2 ? canCompose : true;

  /** The step after Compose: written campaigns confirm what the model understood first. */

  if (!open) return null;

  const reset = (): void => {
    setStep(0);
    setLocationIds([]);
    setRecipientIds([]);
    setLocationQuery("");
    setRecipientQuery("");
    setStarterSlug("");
    setMode("written");
    setMessages([""]);
    setDraft(null);
    setIntent("");
    setCorrecting(false);
  };

  /**
   * Ask the model to read the written message back in plain language. The agent approves that
   * sentence, or corrects it and we ask again from their correction. Approving is what produces the
   * context — there is no way to send a written campaign without one.
   */
  const handleDraftContext = async (): Promise<void> => {
    if (!Member.orgId || customMessages.length === 0) return;
    setDrafting(true);
    try {
      const result = await API.SnowyCampaigns.draftContextForOrg(Member.orgId, {
        message: customMessages.join("\n\n"),
        ...(intent.trim() ? { intent: intent.trim() } : {})
      });
      if (!result) {
        AlertsModel.errorToast("Could not read that back — try again, or reword the message.");
        return;
      }
      setDraft(result);
    } catch (err) {
      AlertsModel.errorToast(err instanceof Error && err.message ? err.message : "Could not read that back");
    } finally {
      setDrafting(false);
    }
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const handleSend = async (): Promise<void> => {
    if (!Member.orgId) return;
    if (written && !draft) return;
    if (!written && !starter) return;
    setSending(true);
    try {
      const chosen = recipients.filter((r) => recipientIds.includes(r.userId));
      const campaign = await API.SnowyCampaigns.create(Member.orgId, {
        // A written campaign has no starter; its context is the one just approved.
        ...(written
          ? { useDefault: false, messages: customMessages, contextText: draft?.contextText, memberContext: draft?.memberContext }
          : {
              starterSlug: starter?.slug,
              useDefault: !editedStarter,
              ...(editedStarter ? { messages: customMessages } : {})
            }),
        recipients: chosen.map((r) => ({ userId: r.userId, locationId: r.locationId }))
      });
      AlertsModel.successToast(`Campaign queued for ${campaign.recipientCount} recipient${campaign.recipientCount === 1 ? "" : "s"}`);
      handleClose();
      navigate("/crm/sms-campaigns");
    } catch (err) {
      AlertsModel.errorToast(err instanceof Error && err.message ? err.message : "Could not send the campaign");
    } finally {
      setSending(false);
    }
  };

  const toggle = (list: number[], id: number): number[] => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <Modal
      isOpen={open}
      onClose={(isOpen) => !isOpen && handleClose()}
      onEscapeKeyDown={(event) => {
        if (pickerOpenRef.current) event.preventDefault();
      }}
      title="New SMS Campaign"
      className="w-[640px]"
      hasTitleBorder
    >
      <div className="flex flex-col gap-4 p-4">
        <ol className="flex gap-2 text-xs">
          {STEP_LABELS.map((label, i) => (
            <li
              key={label}
              className={filterClasses(
                "rounded-full px-2 py-0.5",
                i === step ? "bg-orange-500 text-white" : i < step ? "bg-grey-200 text-grey-800" : "bg-grey-100 text-grey-500"
              )}
            >
              {i + 1}. {label}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="flex flex-col gap-2">
            <p className="body-sm text-grey-600">Which locations&apos; customers should receive this campaign?</p>
            <Input value={locationQuery} placeholder="Search locations…" onChange={(e) => setLocationQuery(e.target.value)} />
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {shownLocations.map((loc) => (
                <label key={loc.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-grey-50">
                  <Checkbox checked={locationIds.includes(loc.id)} onCheckedChange={() => setLocationIds(toggle(locationIds, loc.id))} />
                  <span className="body-sm">{loc.name}</span>
                </label>
              ))}
              {shownLocations.length === 0 && (
                <p className="body-sm text-grey-500">{locations.length === 0 ? "No locations available." : "No locations match."}</p>
              )}
            </div>
            {locationIds.length > 0 && <p className="body-sm text-grey-500">{locationIds.length} selected</p>}
          </div>
        )}

        {step === 1 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="body-sm text-grey-600">
                {isBroadcastTenantsLoading
                  ? "Loading customers…"
                  : `${recipients.length} customer${recipients.length === 1 ? "" : "s"} with a phone number`}
              </p>
              {!isBroadcastTenantsLoading && shownRecipients.length > 0 && (
                <Button
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.GHOST}
                  onClick={() => {
                    // Operates on what's SHOWN: filter to Addis → Select all selects Addis's people.
                    // Clearing a filter never clears selections made under it.
                    const shown = shownRecipients.map((r) => r.userId);
                    const allShownSelected = shown.every((id) => recipientIds.includes(id));
                    setRecipientIds(allShownSelected ? recipientIds.filter((id) => !shown.includes(id)) : [...new Set([...recipientIds, ...shown])]);
                  }}
                >
                  {shownRecipients.every((r) => recipientIds.includes(r.userId)) ? "Clear all" : "Select all"}
                  {recipientQuery.trim() ? " shown" : ""}
                </Button>
              )}
            </div>
            <Input value={recipientQuery} placeholder="Search by name or phone…" onChange={(e) => setRecipientQuery(e.target.value)} />
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {isBroadcastTenantsLoading && <Spinner />}
              {!isBroadcastTenantsLoading &&
                shownRecipients.map((r) => (
                  <label key={r.userId} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-grey-50">
                    <Checkbox checked={recipientIds.includes(r.userId)} onCheckedChange={() => setRecipientIds(toggle(recipientIds, r.userId))} />
                    <span className="body-sm">{r.name || [r.firstName, r.lastName].filter(Boolean).join(" ") || `User ${r.userId}`}</span>
                    <span className="body-sm ml-auto font-mono text-grey-500">{r.phone}</span>
                  </label>
                ))}
              {!isBroadcastTenantsLoading && shownRecipients.length === 0 && (
                <p className="body-sm text-grey-500">{recipients.length === 0 ? "No customers with a phone number." : "No customers match."}</p>
              )}
            </div>
            {recipientIds.length > 0 && <p className="body-sm text-grey-500">{recipientIds.length} selected</p>}
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-3">
            {starters === null && <Spinner />}
            {starters !== null && (
              <>
                <div className="flex gap-3">
                  <ModeCard
                    active={written}
                    title="Write your own"
                    subtitle="Compose a message; we'll confirm what it's about"
                    onClick={() => {
                      setMode("written");
                      setMessages([""]);
                    }}
                  />
                  <ModeCard
                    active={!written}
                    title="Use a starter"
                    subtitle="Pick one of the campaign messages we already send"
                    onClick={() => {
                      setMode("starter");
                      setDraft(null);
                      if (starter) setMessages(starter.verbatimMessages.length > 0 ? starter.verbatimMessages : [""]);
                    }}
                  />
                </div>

                {!written && starters.length === 0 && (
                  <p className="body-sm text-grey-600">
                    No SMS starter is published yet. Write your own message instead, or ask a Swivl admin to publish one in AI Studio.
                  </p>
                )}

                {!written && starters.length > 0 && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-grey-600 uppercase">Conversation Starter</label>
                    <Select
                      options={starters.map((s): SelectItem => ({ value: s.slug, label: s.label }))}
                      selectedValues={starterSlug ? [starterSlug] : []}
                      hideClear
                      onChange={(vals) => {
                        const picked = starters.find((s) => s.slug === vals[0]);
                        setStarterSlug(vals[0]);
                        // Picking a starter fills the message area; the text stays editable.
                        if (picked) setMessages(picked.verbatimMessages.length > 0 ? picked.verbatimMessages : [""]);
                      }}
                    />
                  </div>
                )}

                {(written || starters.length > 0) && (
                  <div className="flex flex-col gap-2">
                    {messages.map((m, i) => (
                      <VariableTypeahead
                        key={i}
                        label={`Message ${i + 1}`}
                        value={m}
                        maxLength={CAMPAIGN_MESSAGE_MAX_CHARS}
                        placeholder="Type {{ to insert a variable"
                        onMenuOpenChange={(isOpen) => {
                          pickerOpenRef.current = isOpen;
                        }}
                        onChange={(next) => {
                          setMessages(messages.map((x, j) => (j === i ? next : x)));
                          // The message changed, so the read-back no longer describes it.
                          setDraft(null);
                        }}
                      />
                    ))}
                    {messages.length < CAMPAIGN_MAX_MESSAGES && (
                      <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setMessages([...messages, ""])}>
                        + Add message
                      </Button>
                    )}
                    <p className="body-sm text-grey-500">
                      Sent word for word. A message that runs long after variables are filled in is split into several texts.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-2">
            <Row label="Recipients" value={`${recipientIds.length} at ${locationIds.length} location${locationIds.length === 1 ? "" : "s"}`} />
            <Row label="Message" value={written ? "Written for this campaign" : `${starter?.label ?? ""}${editedStarter ? " (edited)" : ""}`} />
            <div className="flex flex-col gap-1 rounded-md border border-grey-200 bg-grey-50 p-3">
              {customMessages.map((m, i) => (
                <p key={i} className="body-sm text-grey-800">
                  {m}
                </p>
              ))}
            </div>

            {/* About: what the bot is told if someone replies, as a plain STATEMENT of what the
                campaign tells customers — never a question (Chuck, 2026-09-04). Written path drafts
                on arrival; a starter carries its own context. */}
            <div className="flex flex-col gap-2 rounded-md border border-orange-200 bg-orange-50 p-3">
              <span className="text-xs font-semibold text-grey-600 uppercase">About</span>
              {!written && <p className="body-sm text-grey-900">{starter?.contextText || "Uses the starter's built-in context."}</p>}
              {written && drafting && (
                <div className="flex items-center gap-3">
                  <Spinner />
                  <p className="body-sm text-grey-600">Working out what this is about…</p>
                </div>
              )}
              {written && !drafting && draft && (
                <>
                  <p className="body-sm text-grey-900">{draft.memberContext}</p>
                  {!correcting && (
                    <button
                      type="button"
                      className="self-start text-xs text-orange-700 underline hover:text-orange-800"
                      onClick={() => {
                        setIntent("");
                        setCorrecting(true);
                      }}
                    >
                      Not what you meant? Describe it
                    </button>
                  )}
                  {correcting && (
                    <div className="flex flex-col gap-2">
                      <textarea
                        className="min-h-[70px] resize-none rounded border border-grey-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
                        placeholder="e.g. It's about a water shut-off, not the gate"
                        value={intent}
                        onChange={(e) => setIntent(e.target.value)}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size={ButtonSize.SMALL}
                          color={ButtonColor.ORANGE}
                          disabled={!intent.trim() || drafting}
                          onClick={() => {
                            setCorrecting(false);
                            void handleDraftContext();
                          }}
                        >
                          Redo
                        </Button>
                        <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setCorrecting(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {written && !drafting && !draft && (
                <div className="flex flex-col gap-2">
                  <p className="body-sm text-red-600">
                    Couldn't work out what this campaign is about. Check that the bot is running, then try again.
                  </p>
                  <div>
                    <Button size={ButtonSize.SMALL} color={ButtonColor.ORANGE} onClickAsync={handleDraftContext}>
                      Retry
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <p className="body-sm text-grey-500">
              Sends immediately. Recipients without a value for a variable in the text are skipped; every outcome is listed on the CRM → SMS Campaigns
              tab.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-grey-200 pt-3">
          <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} disabled={step === 0 || sending} onClick={() => setStep((step - 1) as Step)}>
            Back
          </Button>
          {step < 3 ? (
            <Button
              size={ButtonSize.SMALL}
              color={ButtonColor.ORANGE}
              disabled={!canNext}
              onClick={() => {
                const target = (step + 1) as Step;
                setStep(target);
                // Written path: the About summary drafts the moment Review & Send appears.
                if (target === 3 && written && !draft && !drafting) void handleDraftContext();
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              size={ButtonSize.SMALL}
              color={ButtonColor.ORANGE}
              disabled={sending || drafting || (written && !draft)}
              onClickAsync={handleSend}
            >
              {sending ? "Sending…" : `Send to ${recipientIds.length}`}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

interface ModeCardProps {
  active: boolean;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function ModeCard({ active, title, subtitle, onClick }: ModeCardProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={filterClasses(
        "flex flex-1 cursor-pointer flex-col gap-1 rounded-md border p-3 text-left transition-colors",
        active ? "border-orange-500 bg-orange-50" : "border-grey-200 bg-white hover:border-grey-300"
      )}
    >
      <span className={filterClasses("body-sm-alt", active ? "text-black" : "text-grey-800")}>{title}</span>
      <span className="body-sm text-grey-500">{subtitle}</span>
    </button>
  );
}

interface RowProps {
  label: string;
  value: string;
}

function Row({ label, value }: RowProps): React.ReactElement {
  return (
    <div className="flex items-baseline gap-3">
      <span className="w-24 text-xs font-semibold text-grey-600 uppercase">{label}</span>
      <span className="body-sm text-grey-800">{value}</span>
    </div>
  );
}
